import { FieldValueMap } from "utils/types";
import { LoanFieldWithIds } from "types/loan";
import { PropertyDetailFieldModel } from "providers/loans/property.model";
import moment from "moment";

/**
 * Calculates loan-related values based on input data
 * @param loanValues - Array of loan values with their corresponding field IDs
 * @param loanFields - Array of loan field definitions
 * @param propertyFields - Array of property field definitions
 * @param loan - Loan object containing loan type information
 * @param propertyValues - Array of property values with their corresponding field IDs
 * @returns A map of field IDs to calculated values
 */
export const amplendLoanCalculations = ({
  loanValues,
  loanFields,
  propertyFields,
  loan,
  propertyValues,
}: {
  loanFields: LoanFieldWithIds[];
  loanValues: {
    id: string;
    loan: string;
    value: { field: string };
    loan_field: { id: string };
  }[];
  propertyFields: PropertyDetailFieldModel[];
  propertyValues: {
    id: string;
    value: { field: string };
    property_field: number;
  }[];
  loan: { loan_type: { description: string } };
}): FieldValueMap => {
  // Initialize the result map that will store calculated values
  const calcValues: FieldValueMap = {};

  // Map to store the relationship between loan_value IDs and loan_field IDs
  const fieldmap: Record<string, string> = {};

  // Process loan values
  for (const f of loanValues) {
    // Store the value using the loan_field.id as the key
    if (f?.loan_field?.id) {
      calcValues[f.loan_field.id] = f.value.field;
    }
    // For loan values, map the loan_value ID to the loan_field ID
    fieldmap[String(f.id)] = String(f?.loan_field?.id);
  }

  // Process property values
  for (const f of propertyValues) {
    calcValues[f.property_field] = f.value.field;
    // For property values, the ID is the same
    fieldmap[String(f.property_field)] = String(f?.property_field);
  }

  // Create a map of identifier_key to id for loanFields
  const loanFieldMap: Record<string, string> = {};
  for (const field of loanFields) {
    if (field.identifier_key) {
      loanFieldMap[field.identifier_key] = field.id;
    }
  }

  // Create a map of data_key to id for propertyFields
  const propertyFieldMap: Record<string, number> = {};
  for (const field of propertyFields) {
    if (field.data_key) {
      propertyFieldMap[field.data_key] = field.id;
    }
  }

  // Helper function to safely get a loan field ID
  const getLoanFieldId = (identifierKey: string): string => {
    return loanFieldMap[identifierKey] || "";
  };

  // Helper function to safely get numeric values with validation
  const getNumericValue = (fieldId: string | number, defaultValue: number = 0): number => {
    const value = calcValues[fieldId];
    if (value === undefined || value === null) {
      return defaultValue;
    }

    const numValue = Number(value);
    return isNaN(numValue) ? defaultValue : numValue;
  };

  // Helper function to safely perform division
  const safeDivide = (numerator: number, denominator: number): number => {
    return denominator !== 0 ? numerator / denominator : 0;
  };

  // Helper function to format currency values
  const formatCurrency = (value: number): string => {
    return value.toFixed(2);
  };

  // Helper function to safely get a date value
  const getDateValue = (fieldId: string | number): string => {
    const value = calcValues[fieldId];
    return value || "";
  };

  // ===== LOAN FIELD IDS =====
  // Credit report dates
  const credit_report_date_1_id = getLoanFieldId("credit_report_date_1");
  const credit_report_date_2_id = getLoanFieldId("credit_report_date_2");
  const credit_report_date_3_id = getLoanFieldId("credit_report_date_3");

  // Loan dates
  const origination_date_id = getLoanFieldId("origination_date");
  const target_closing_date_id = getLoanFieldId("target_closing_date");
  const appraisal_date_id = getLoanFieldId("appraisal_date");
  const first_payment_date_id = getLoanFieldId("first_payment_date");
  const maturity_date_id = getLoanFieldId("maturity_date");

  // Loan terms
  const loan_term_months_id = getLoanFieldId("loan_term_months");
  const interest_rate_id = getLoanFieldId("interest_rate");
  const interest_only_period_months_id = getLoanFieldId("interest_only_period_months");

  // Loan amounts
  const initial_loan_amount_id = getLoanFieldId("initial_loan_amount");
  const financed_budget_amount_id = getLoanFieldId("financed_budget_amount");
  const total_loan_amount_id = getLoanFieldId("total_loan_amount");
  const total_origination_points_id = getLoanFieldId("total_origination_points");

  // Property values
  const as_is_appraisal_value_id = getLoanFieldId("as_is_appraisal_value");
  const after_repair_appraisal_value_id = getLoanFieldId("after_repair_appraisal_value");

  // Monthly payments and rents
  const monthly_effective_rent_id = getLoanFieldId("monthly_effective_rent");
  const monthly_payment_id = getLoanFieldId("monthly_payment");
  const monthly_market_rent_id = getLoanFieldId("monthly_market_rent");
  const monthly_in_place_lease_rent_id = getLoanFieldId("monthly_in_place_lease_rent");
  const total_monthly_effective_rent_id = getLoanFieldId("total_monthly_effective_rent");

  // Property occupancy
  const unit_occupied_id = getLoanFieldId("unit_occupied");
  const number_of_units_id = getLoanFieldId("number_of_units");

  // Budget and draws
  const total_rehab_budget_id = getLoanFieldId("total_rehab_budget");
  const approved_draw_1_id = getLoanFieldId("approved_draw_1");
  const approved_draw_2_id = getLoanFieldId("approved_draw_2");
  const approved_draw_3_id = getLoanFieldId("approved_draw_3");
  const approved_draw_4_id = getLoanFieldId("approved_draw_4");
  const approved_draw_5_id = getLoanFieldId("approved_draw_5");
  const approved_draw_6_id = getLoanFieldId("approved_draw_6");
  const approved_draw_7_id = getLoanFieldId("approved_draw_7");
  const approved_draw_8_id = getLoanFieldId("approved_draw_8");
  const approved_draw_cumulative_id = getLoanFieldId("approved_draw_cumulative");
  const remaining_budget_amount_id = getLoanFieldId("remaining_budget_amount");

  // Fees and costs
  const background_credit_report_id = getLoanFieldId("background_credit_report");
  const legal_fee_id = getLoanFieldId("legal_fee");
  const underwriting_processing_fee_id = getLoanFieldId("underwriting_processing_fee");
  const title_review_handling_fee_id = getLoanFieldId("title_review_handling_fee");
  const flood_certification_id = getLoanFieldId("flood_certification");
  const overnight_mail_fee_id = getLoanFieldId("overnight_mail_fee");
  const wire_fee_id = getLoanFieldId("wire_fee");
  const feasibility_study_id = getLoanFieldId("feasibility_study");
  const valuation_review_id = getLoanFieldId("valuation_review");
  const estimated_title_fees_id = getLoanFieldId("estimated_title_fees");
  const estimated_insurance_cost_id = getLoanFieldId("estimated_insurance_cost");
  const estimated_appraisal_cost_id = getLoanFieldId("estimated_appraisal_cost");
  const annual_insurance_premium_id = getLoanFieldId("annual_insurance_premium");
  const annual_property_taxes_id = getLoanFieldId("annual_property_taxes");
  const annual_hoa_id = getLoanFieldId("annual_hoa");
  const origination_fee_id = getLoanFieldId("origination_fee");
  const prepaid_daily_interest_charge_id = getLoanFieldId("prepaid_daily_interest_charge");

  // Calculated values
  const appraisal_expiration_date_id = getLoanFieldId("appraisal_expiration_date");
  const as_is_ltv_id = getLoanFieldId("as_is_ltv");
  const credit_expiration_date_1_id = getLoanFieldId("credit_expiration_date_1");
  const credit_expiration_date_2_id = getLoanFieldId("credit_expiration_date_2");
  const credit_expiration_date_3_id = getLoanFieldId("credit_expiration_date_3");
  const dscr_id = getLoanFieldId("dscr");
  const down_payment_id = getLoanFieldId("down_payment");
  const estimated_cash_due_at_closing_id = getLoanFieldId("estimated_cash_due_at_closing");
  const ltarv_id = getLoanFieldId("ltarv");
  const ltc_id = getLoanFieldId("ltc");
  const project_profitability_id = getLoanFieldId("project_profitability");
  const cost_basis_id = getLoanFieldId("cost_basis");
  const purchase_price_id = getLoanFieldId("purchase_price");

  // ===== PROPERTY FIELD IDS =====
  // No property fields needed for this calculation

  // ===== CALCULATIONS =====

  // Calculate expiration dates
  if (appraisal_date_id) {
    const appraisalDate = getDateValue(appraisal_date_id);
    if (appraisalDate) {
      const expirationDate = moment(appraisalDate).add(12, "months").format("YYYY-MM-DD");
      calcValues[appraisal_expiration_date_id] = expirationDate;
    }
  }

  // Calculate credit report expiration dates
  const creditReportDates = [
    { dateId: credit_report_date_1_id, expirationId: credit_expiration_date_1_id },
    { dateId: credit_report_date_2_id, expirationId: credit_expiration_date_2_id },
    { dateId: credit_report_date_3_id, expirationId: credit_expiration_date_3_id },
  ];

  creditReportDates.forEach(({ dateId, expirationId }) => {
    if (dateId) {
      const reportDate = getDateValue(dateId);
      if (reportDate) {
        const expirationDate = moment(reportDate).add(120, "days").format("YYYY-MM-DD");
        calcValues[expirationId] = expirationDate;
      }
    }
  });

  // Loan dates calculations
  if (origination_date_id && loan_term_months_id && maturity_date_id) {
    const originationDate = getDateValue(origination_date_id);
    if (originationDate) {
      const firstPaymentDate = moment(originationDate).add(2, "months").startOf("month");
      calcValues[first_payment_date_id] = firstPaymentDate.format("YYYY-MM-DD");

      // Calculate Maturity Date
      const loanTermMonths = getNumericValue(loan_term_months_id);
      const maturityDate = firstPaymentDate.add(loanTermMonths, "months");
      calcValues[maturity_date_id] = maturityDate.format("YYYY-MM-DD");
    }
  }

  // Calculate loan amounts and percentages
  const initialLoanAmount = getNumericValue(initial_loan_amount_id);
  const financedBudgetAmount = getNumericValue(financed_budget_amount_id);
  const totalLoanAmount = initialLoanAmount + financedBudgetAmount;
  calcValues[total_loan_amount_id] = formatCurrency(totalLoanAmount);

  // Monthly payment calculations
  if (monthly_payment_id && initial_loan_amount_id && interest_rate_id) {
    const interestRate = getNumericValue(interest_rate_id) / 100;
    const monthlyInterestRate = interestRate / 12;

    // Short-term
    if (loan?.loan_type?.description === "Short-Term") {
      calcValues[monthly_payment_id] = formatCurrency((initialLoanAmount * interestRate) / 12);
    } else if (loan?.loan_type?.description === "Long-Term") {
      // Monthly PITIA (i.e. Principal, Interest, Tax, Insurance, and Association Fee)
      const interestOnlyPeriodMonths = getNumericValue(interest_only_period_months_id);

      let principalAndInterest: number;

      if (interestOnlyPeriodMonths > 0) {
        // Calculate interest-only payment
        principalAndInterest = initialLoanAmount * monthlyInterestRate;
      } else {
        // Calculate the fully amortized payment
        principalAndInterest =
          (initialLoanAmount * monthlyInterestRate) /
          (1 - Math.pow(1 + monthlyInterestRate, -interestOnlyPeriodMonths));
      }

      const monthlyInsurance = safeDivide(getNumericValue(annual_insurance_premium_id), 12);
      const monthlyTax = safeDivide(getNumericValue(annual_property_taxes_id), 12);
      const monthlyHOA = safeDivide(getNumericValue(annual_hoa_id), 12);

      calcValues[monthly_payment_id] = formatCurrency(
        principalAndInterest + monthlyInsurance + monthlyTax + monthlyHOA
      );
    }
  }

  // Monthly effective rent calculations
  if (monthly_effective_rent_id && total_monthly_effective_rent_id) {
    const monthlyEffectiveRent = getNumericValue(monthly_effective_rent_id);
    calcValues[total_monthly_effective_rent_id] = formatCurrency(monthlyEffectiveRent);

    if (monthly_payment_id && dscr_id) {
      const monthlyPayment = getNumericValue(monthly_payment_id);
      if (monthlyPayment > 0) {
        const dscr = safeDivide(monthlyEffectiveRent, monthlyPayment);
        calcValues[dscr_id] = formatCurrency(dscr);
      }
    }
  }

  // Calculate LTV and LTC
  const asIsAppraisalValue = getNumericValue(as_is_appraisal_value_id);
  const afterRepairAppraisalValue = getNumericValue(after_repair_appraisal_value_id);
  const totalRehabBudget = getNumericValue(total_rehab_budget_id);

  if (asIsAppraisalValue > 0) {
    const asIsLtv = safeDivide(totalLoanAmount, asIsAppraisalValue) * 100;
    calcValues[as_is_ltv_id] = formatCurrency(asIsLtv);
  }

  if (afterRepairAppraisalValue > 0) {
    const ltarv = safeDivide(totalLoanAmount, afterRepairAppraisalValue) * 100;
    calcValues[ltarv_id] = formatCurrency(ltarv);
  }

  if (totalRehabBudget > 0) {
    const ltc = safeDivide(totalLoanAmount, asIsAppraisalValue + totalRehabBudget) * 100;
    calcValues[ltc_id] = formatCurrency(ltc);
  }

  // Purchase price calculations
  if (purchase_price_id && initial_loan_amount_id && down_payment_id) {
    const purchasePrice = getNumericValue(purchase_price_id);
    const initialLoanAmount = getNumericValue(initial_loan_amount_id);
    const downPayment = Math.max(purchasePrice - initialLoanAmount, 0);
    calcValues[down_payment_id] = formatCurrency(downPayment);
  }

  // Origination fee calculations
  if (total_loan_amount_id && total_origination_points_id && origination_fee_id) {
    const totalOriginationPoints = getNumericValue(total_origination_points_id);
    calcValues[origination_fee_id] = formatCurrency(
      totalLoanAmount * (totalOriginationPoints / 100)
    );
  }

  // Prepaid interest calculations
  if (
    target_closing_date_id &&
    initial_loan_amount_id &&
    interest_rate_id &&
    prepaid_daily_interest_charge_id
  ) {
    const targetClosingDate = getDateValue(target_closing_date_id);
    if (targetClosingDate) {
      const closingDate = moment(targetClosingDate);
      const daysInMonth = closingDate.daysInMonth();
      const daysToEndOfMonth = daysInMonth - closingDate.date() + 1;
      const interestRate = getNumericValue(interest_rate_id) / 100;

      calcValues[prepaid_daily_interest_charge_id] = formatCurrency(
        daysToEndOfMonth * ((initialLoanAmount * interestRate) / 360)
      );
    }
  }

  // Unit occupied calculations
  if (unit_occupied_id && number_of_units_id) {
    const unitOccupied = getNumericValue(unit_occupied_id);
    const numberOfUnits = getNumericValue(number_of_units_id);
    if (numberOfUnits === 1) {
      calcValues[unit_occupied_id] = unitOccupied >= 1 ? "Y" : "N";
    } else if (numberOfUnits === 2) {
      calcValues[unit_occupied_id] = unitOccupied >= 2 ? "Y" : "N";
    } else if (numberOfUnits === 3) {
      calcValues[unit_occupied_id] = unitOccupied >= 2 ? "Y" : "N";
    } else if (numberOfUnits === 4) {
      calcValues[unit_occupied_id] = unitOccupied >= 3 ? "Y" : "N";
    }
  }

  // Monthly effective rent calculations
  if (
    monthly_market_rent_id &&
    monthly_in_place_lease_rent_id &&
    unit_occupied_id &&
    monthly_effective_rent_id
  ) {
    const monthlyMarketRent = getNumericValue(monthly_market_rent_id);
    const monthlyInPlaceLeaseRent = getNumericValue(monthly_in_place_lease_rent_id);
    const unitOccupied = calcValues[unit_occupied_id] === "Y";
    calcValues[monthly_effective_rent_id] = unitOccupied
      ? formatCurrency(Math.min(monthlyMarketRent * 1.05, monthlyInPlaceLeaseRent))
      : formatCurrency(monthlyMarketRent);
  }

  // Cost basis calculations
  if (purchase_price_id && cost_basis_id) {
    const purchasePrice = getNumericValue(purchase_price_id);

    // Calculate eligible closing costs based on the formula
    const eligibleClosingCosts =
      getNumericValue(background_credit_report_id) +
      getNumericValue(underwriting_processing_fee_id) +
      getNumericValue(flood_certification_id) +
      getNumericValue(wire_fee_id) +
      getNumericValue(valuation_review_id) +
      getNumericValue(estimated_insurance_cost_id) +
      getNumericValue(prepaid_daily_interest_charge_id) +
      getNumericValue(legal_fee_id) +
      getNumericValue(title_review_handling_fee_id) +
      getNumericValue(overnight_mail_fee_id) +
      getNumericValue(feasibility_study_id) +
      getNumericValue(estimated_title_fees_id) +
      getNumericValue(estimated_appraisal_cost_id);

    calcValues[cost_basis_id] = formatCurrency(purchasePrice + eligibleClosingCosts);
  }

  // Project profitability calculations
  if (
    after_repair_appraisal_value_id &&
    cost_basis_id &&
    total_rehab_budget_id &&
    project_profitability_id
  ) {
    const costBasis = getNumericValue(cost_basis_id);
    if (costBasis > 0) {
      const profitability = safeDivide(afterRepairAppraisalValue - costBasis, costBasis) * 100;
      calcValues[project_profitability_id] = formatCurrency(profitability);
    }
  }

  // Approved draw cumulative calculations
  if (approved_draw_cumulative_id) {
    const approvedDraws = [
      approved_draw_1_id,
      approved_draw_2_id,
      approved_draw_3_id,
      approved_draw_4_id,
      approved_draw_5_id,
      approved_draw_6_id,
      approved_draw_7_id,
      approved_draw_8_id,
    ].reduce((sum, drawId) => sum + getNumericValue(drawId), 0);

    calcValues[approved_draw_cumulative_id] = formatCurrency(approvedDraws);
  }

  // Remaining budget calculations
  if (remaining_budget_amount_id) {
    const approvedDrawCumulative = getNumericValue(approved_draw_cumulative_id);
    calcValues[remaining_budget_amount_id] = formatCurrency(
      financedBudgetAmount - approvedDrawCumulative
    );
  }

  // Calculate estimated cash due at closing
  const totalFees = [
    background_credit_report_id,
    legal_fee_id,
    underwriting_processing_fee_id,
    title_review_handling_fee_id,
    flood_certification_id,
    overnight_mail_fee_id,
    wire_fee_id,
    feasibility_study_id,
    valuation_review_id,
    estimated_title_fees_id,
    estimated_insurance_cost_id,
    estimated_appraisal_cost_id,
  ].reduce((sum, feeId) => sum + getNumericValue(feeId), 0);

  const prepaidItems = [
    annual_insurance_premium_id,
    annual_property_taxes_id,
    annual_hoa_id,
  ].reduce((sum, itemId) => sum + getNumericValue(itemId), 0);

  const originationFee = getNumericValue(origination_fee_id);
  const prepaidInterest = getNumericValue(prepaid_daily_interest_charge_id);
  const downPayment = getNumericValue(down_payment_id);

  const estimatedCashDue =
    totalFees + prepaidItems + originationFee + prepaidInterest + downPayment;
  calcValues[estimated_cash_due_at_closing_id] = formatCurrency(estimatedCashDue);

  return calcValues;
};
